import React, { useState, useRef } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";
import authApi from "../../../../service/IdApi";
import PageLoading from "../../../../components/PageLoading";
import PageLoadError from "../../../../components/PageLoadError";
import PageHeading from "../../../../components/PageHeading";
import ProviderRecommendationsDialog from "./ProviderRecommendationsDialog";
import RecommendationsSubNav from "./RecommendationsSubNav";
import { useAppContext } from "../../../../context/appContext";

const NEXT_EMAIL_DATE_FILTER_OPTIONS = [
    { label: "Date set", value: "set" },
    { label: "Date not set", value: "not_set" },
    { label: "Both", value: "both" },
];

const ProviderRecommendations = () => {
    const { displayAlert } = useAppContext();
    const menu = useRef(null);
    const [providers, setProviders] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [nameFilter, setNameFilter] = useState("");
    const [nextEmailDateFilter, setNextEmailDateFilter] = useState("set");
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedProviderId, setSelectedProviderId] = useState(null);
    const [selectedProviderName, setSelectedProviderName] = useState(null);
    const [actionProvider, setActionProvider] = useState(null);
    const [rebuildConfirmVisible, setRebuildConfirmVisible] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const [sendingEmails, setSendingEmails] = useState(false);
    const [sendEmailDialogVisible, setSendEmailDialogVisible] = useState(false);
    const [sendEmailPreview, setSendEmailPreview] = useState(null);
    const [sendEmailPreviewLoading, setSendEmailPreviewLoading] = useState(false);
    const [sendingProviderEmail, setSendingProviderEmail] = useState(false);

    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 50,
        page: 1,
        sortField: "nextSiteRecommendationEmailAt",
        sortOrder: 1,
    });

    let params = "?first=" + lazyState.first + "&rows=" + lazyState.rows
        + "&nextEmailDate=" + nextEmailDateFilter;
    if (lazyState.sortField) {
        params += "&sortField=" + lazyState.sortField;
        if (lazyState.sortOrder) {
            params += "&sortOrder=" + lazyState.sortOrder;
        }
    }

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["ProviderRecommendations", params], async () => {
        var searchBody = {};
        searchBody.first = lazyState.first;
        searchBody.rows = lazyState.rows;
        searchBody.sortField = lazyState.sortField;
        searchBody.sortOrder = lazyState.sortOrder;
        if (nameFilter !== "") {
            searchBody.name = nameFilter;
        }
        if (nextEmailDateFilter === "set") {
            searchBody.hasNextSiteRecommendationEmailAt = true;
        } else if (nextEmailDateFilter === "not_set") {
            searchBody.hasNextSiteRecommendationEmailAt = false;
        }
        const result = await authApi.post("admin/recommendations/providers/list", searchBody);
        setProviders(result.data.providers);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
        return data;
    });

    const pageHeader = (
        <PageHeading pageTitle="Admin - Provider Recommendations">
            <RecommendationsSubNav active="providers" />
        </PageHeading>
    );

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    if (!loaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    const onPage = (event) => {
        setlazyState(event);
    };

    const onSort = (event) => {
        setlazyState(event);
    };

    const handleSearch = () => {
        refetch();
    };

    const clearFilters = () => {
        setNameFilter("");
        setNextEmailDateFilter("set");
        setTimeout(() => {
            refetch();
        }, 100);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const nameClicked = (e, row) => {
        e.preventDefault();
        setSelectedProviderId(row.providerId);
        setSelectedProviderName(row.name);
        setDialogVisible(true);
    };

    const nameBodyTemplate = (rowData) => {
        return (
            <div className="">
                {rowData.name}
            </div>
        );
    };

    const formatNextEmailAt = (value) => {
        if (!value) {
            return "—";
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return "—";
        }
        return date.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const nextEmailBodyTemplate = (rowData) => {
        return formatNextEmailAt(rowData.nextSiteRecommendationEmailAt);
    };

    const subscribedBodyTemplate = (rowData) => {
        return rowData.siteRecommendationsEmailEnabled !== false ? "Yes" : "No";
    };

    const rowActions = [
        {
            label: "Rebuild Recommendations",
            icon: "pi pi-refresh",
            command: () => {
                setRebuildConfirmVisible(true);
            },
        },
        {
            label: "Send Next Recommendation Email",
            icon: "pi pi-envelope",
            command: () => {
                openSendEmailDialog();
            },
        },
    ];

    const openSendEmailDialog = async () => {
        if (!actionProvider) {
            return;
        }
        setSendEmailPreview(null);
        setSendEmailDialogVisible(true);
        setSendEmailPreviewLoading(true);
        try {
            const result = await authApi.get(
                "admin/recommendations/providers/" + encodeURIComponent(actionProvider.providerId) + "/email/preview"
            );
            setSendEmailPreview(result.data);
        } catch (err) {
            console.error(err);
            displayAlert("error", "Unexpected Error", "Failed to load recommendation email preview.");
            setSendEmailDialogVisible(false);
        } finally {
            setSendEmailPreviewLoading(false);
        }
    };

    const closeSendEmailDialog = () => {
        setSendEmailDialogVisible(false);
        setSendEmailPreview(null);
    };

    const sendProviderRecommendationEmail = async () => {
        if (!actionProvider) {
            return;
        }
        setSendingProviderEmail(true);
        try {
            const result = await authApi.post(
                "admin/recommendations/providers/" + encodeURIComponent(actionProvider.providerId) + "/email/send"
            );
            const sendResult = result.data;
            if (sendResult?.sent) {
                const recipients =
                    sendResult.recipientEmails?.length > 0
                        ? sendResult.recipientEmails.join(", ")
                        : sendResult.recipientEmail;
                const count = sendResult.emailsSentCount ?? sendResult.recipientEmails?.length ?? 1;
                displayAlert(
                    "success",
                    "Email Sent",
                    `Sent "${sendResult.title}" to ${count} recipient${count === 1 ? "" : "s"}: ${recipients}.`
                );
                closeSendEmailDialog();
                refetch();
            } else {
                displayAlert("info", "Email Not Sent", sendResult?.message || "No recommendation email was sent.");
            }
        } catch (err) {
            console.error(err);
            displayAlert("error", "Unexpected Error", "Failed to send recommendation email. Please try again later.");
        } finally {
            setSendingProviderEmail(false);
        }
    };

    const previewRecipientEmails =
        sendEmailPreview?.recipientEmails?.length > 0
            ? sendEmailPreview.recipientEmails
            : sendEmailPreview?.recipientEmail
                ? [sendEmailPreview.recipientEmail]
                : [];
    const siteRecommendationsEmailEnabled = sendEmailPreview?.siteRecommendationsEmailEnabled !== false;
    const isSiteConversionEmail = sendEmailPreview?.emailKind === "site_conversion";
    const canSendProviderEmail =
        siteRecommendationsEmailEnabled &&
        previewRecipientEmails.length > 0 &&
        (isSiteConversionEmail || !!sendEmailPreview?.recommendation);

    const showActionMenu = (e, rowData) => {
        setActionProvider(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const rebuildRecommendations = async () => {
        if (!actionProvider) return;
        setRebuilding(true);
        try {
            await authApi.post("admin/recommendations/providers/" + encodeURIComponent(actionProvider.providerId) + "/rebuild");
            displayAlert("success", "Recommendations Rebuilt", `Recommendations were rebuilt for ${actionProvider.name}.`);
            refetch();
        } catch (err) {
            console.error(err);
            displayAlert("error", "Unexpected Error", "Failed to rebuild recommendations. Please try again later.");
        } finally {
            setRebuilding(false);
            setRebuildConfirmVisible(false);
        }
    };

    const sendRecommendationEmails = async () => {
        setSendingEmails(true);
        try {
            const result = await authApi.post("admin/recommendations/emails/send");
            const sentCount = result.data?.sentCount ?? 0;
            if (sentCount === 0) {
                displayAlert("info", "Send Emails", "No eligible providers to email.");
            } else {
                displayAlert("success", "Send Emails", `Sent ${sentCount} recommendation email${sentCount === 1 ? "" : "s"}.`);
            }
            refetch();
        } catch (err) {
            console.error(err);
            displayAlert("error", "Unexpected Error", "Failed to send recommendation emails. Please try again later.");
        } finally {
            setSendingEmails(false);
        }
    };

    const onRowSelect = (event) => {
        const target = event.originalEvent.target;
        if (target.closest("button")) {
            return;
        }
        setSelectedProviderId(event.data.providerId);
        setSelectedProviderName(event.data.name);
        setDialogVisible(true);
    };

    return (
        <div>
            {pageHeader}
            <ProviderRecommendationsDialog
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                providerId={selectedProviderId}
                providerName={selectedProviderName}
            />
            <Menu model={rowActions} popup ref={menu} />
            <Dialog
                visible={rebuildConfirmVisible}
                style={{ width: "450px" }}
                header="Rebuild Recommendations"
                modal
                footer={
                    <div>
                        <Button label="Cancel" icon="pi pi-times" onClick={() => setRebuildConfirmVisible(false)} className="p-button-text" disabled={rebuilding} />
                        <Button label="Rebuild" icon="pi pi-refresh" onClick={rebuildRecommendations} loading={rebuilding} autoFocus />
                    </div>
                }
                onHide={() => setRebuildConfirmVisible(false)}
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                    <span>
                        Rebuild recommendations for <strong>{actionProvider?.name}</strong>? In development this wipes and rebuilds the full set; in production completed and dismissed recommendations are preserved.
                    </span>
                </div>
            </Dialog>
            <Dialog
                visible={sendEmailDialogVisible}
                style={{ width: "550px" }}
                header="Send Next Recommendation Email"
                modal
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={closeSendEmailDialog}
                            className="p-button-text"
                            disabled={sendingProviderEmail}
                        />
                        <Button
                            label="Send"
                            icon="pi pi-send"
                            onClick={sendProviderRecommendationEmail}
                            loading={sendingProviderEmail}
                            disabled={sendEmailPreviewLoading || !canSendProviderEmail}
                            autoFocus
                        />
                    </div>
                }
                onHide={closeSendEmailDialog}
            >
                {sendEmailPreviewLoading ? (
                    <div className="flex justify-content-center py-4">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
                    </div>
                ) : sendEmailPreview ? (
                    <div className="flex flex-column gap-3">
                        <div>
                            <div className="font-semibold mb-1">Provider</div>
                            <div>{sendEmailPreview.providerName || actionProvider?.name || "—"}</div>
                        </div>
                        {!siteRecommendationsEmailEnabled ? (
                            <div className="flex align-items-start">
                                <i className="pi pi-ban mr-2 mt-1" />
                                <span>
                                    This provider is not subscribed to site recommendation emails.
                                    Re-enable site recommendations for the account before sending.
                                </span>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <div className="font-semibold mb-1">
                                        Recipient Email{previewRecipientEmails.length === 1 ? "" : "s"}
                                    </div>
                                    <div>
                                        {previewRecipientEmails.length > 0
                                            ? previewRecipientEmails.join(", ")
                                            : "No recipient emails found for this provider."}
                                    </div>
                                </div>
                                {isSiteConversionEmail ? (
                                    <div>
                                        <div className="font-semibold mb-1">Next Email</div>
                                        <div className="font-medium">
                                            {sendEmailPreview.title || "Site conversion"}
                                        </div>
                                        <div className="text-color-secondary mt-2">
                                            Profile-to-site conversion drip email (trial / upgrade notice).
                                        </div>
                                    </div>
                                ) : sendEmailPreview.recommendation ? (
                                    <div>
                                        <div className="font-semibold mb-1">Next Recommendation</div>
                                        <div className="font-medium">{sendEmailPreview.recommendation.title}</div>
                                        <div className="text-color-secondary mt-2">{sendEmailPreview.recommendation.description}</div>
                                        <div className="text-sm text-color-secondary mt-2">
                                            {(sendEmailPreview.recommendation.productFriendlyName ||
                                                sendEmailPreview.recommendation.product) && (
                                                    <span>
                                                        Product:{" "}
                                                        {sendEmailPreview.recommendation.productFriendlyName ||
                                                            sendEmailPreview.recommendation.product}{" "}
                                                        ·{" "}
                                                    </span>
                                                )}
                                            Score: {sendEmailPreview.recommendation.score}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex align-items-start">
                                        <i className="pi pi-info-circle mr-2 mt-1" />
                                        <span>No eligible emailable recommendation is available for this provider right now.</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : null}
            </Dialog>
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full flex-wrap align-items-center">
                        <div className="flex flex-row flex-wrap align-items-center flex-grow-1">
                            <div className="font-bold mr-3 mb-2">
                                Filter By
                            </div>
                            <div className="flex mr-3 mb-2">
                                <InputText
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    placeholder="Company Name"
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div className="flex mr-3 mb-2">
                                <Dropdown
                                    value={nextEmailDateFilter}
                                    options={NEXT_EMAIL_DATE_FILTER_OPTIONS}
                                    onChange={(e) => setNextEmailDateFilter(e.value)}
                                    placeholder="Next Email Date"
                                    className="w-12rem"
                                />
                            </div>
                            <div className="flex mb-2">
                                <Button
                                    icon="pi pi-search"
                                    className="p-button-primary"
                                    onClick={handleSearch}
                                    tooltip="Search"
                                    loading={isLoading || isFetching}
                                    label="Search"
                                />
                            </div>
                            <div className="flex mb-2">
                                <Button onClick={clearFilters} className="p-button-text" icon="pi pi-times" tooltip="Clear Filter" />
                            </div>
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end gap-2 mb-2">
                            {/* TODO we should enable bulk emailing, but we might want to closely consider how it should be done.  */}
                            {/* <Button
                                type="button"
                                icon="pi pi-envelope"
                                onClick={sendRecommendationEmails}
                                label="Send Emails"
                                loading={sendingEmails}
                            /> */}
                        </div>
                    </div>
                    <DataTable
                        value={providers}
                        dataKey="providerId"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[25, 50, 100, 500]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} providers"
                        emptyMessage="No providers found."
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || !loaded}
                        size="small"
                        selectionMode="single"
                        onRowSelect={onRowSelect}
                    >
                        <Column body={actionBodyTemplate} header="Actions" style={{ width: "5rem" }} />
                        <Column body={nameBodyTemplate} field="name" header="Name" sortable />
                        <Column field="providerNumber" header="Provider #" />
                        <Column
                            field="siteRecommendationsEmailEnabled"
                            header="Subscribed"
                            body={subscribedBodyTemplate}
                        />
                        <Column
                            field="nextSiteRecommendationEmailAt"
                            header="Next Email"
                            body={nextEmailBodyTemplate}
                            sortable
                        />
                        <Column field="activeCount" header="Active" />
                        <Column field="completedCount" header="Completed" />
                        <Column field="snoozedCount" header="Snoozed" />
                        <Column field="dismissedCount" header="Dismissed" />
                        <Column field="totalCount" header="Total" />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default ProviderRecommendations;
